import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Stack
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import PeopleIcon from '@mui/icons-material/People';
import FolderIcon from '@mui/icons-material/Folder';
import StorageIcon from '@mui/icons-material/Storage';
import AvTimerIcon from '@mui/icons-material/AvTimer';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import { Star as StarIcon } from '@mui/icons-material';

import { Team } from '../../context/OrganizationContext';
import './utils/compatibility-fix';
import { formatStorageAuto } from '../../utils/storage-formatter';

interface TeamSummaryProps {
  team: Team;
  memberCount: number;
  onInviteMember: () => void;
  onManageSubscription: () => void;
  onCreateContent?: () => void;
  subscription?: any;
  usage?: {
    current_member_count: number;
    storage_used_bytes: number;
    content_count: number;
  };
  isLoading?: boolean;
  error?: string;
  canInviteMembers?: boolean;
  canManageSubscription?: boolean;
  canCreateContent?: boolean;
}

const TeamSummary: React.FC<TeamSummaryProps> = ({
  team,
  memberCount,
  onInviteMember,
  onManageSubscription,
  onCreateContent,
  subscription,
  usage,
  isLoading,
  error,
  canInviteMembers = true,
  canManageSubscription = true,
  canCreateContent = true
}) => {
  // Show error state if there's an error loading data
  if (error) {
    return (
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="error" gutterBottom>
              Error Loading Team Data
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {error}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* Team Stats */}
      <Grid xs={12} md={8}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Team Overview
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={3}>
            <Grid xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <PeopleIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4">{memberCount}</Typography>
                <Typography variant="body2" color="text.secondary">Members</Typography>
              </Box>
            </Grid>
            
            <Grid xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <FolderIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4">
                  {isLoading ? <CircularProgress size={24} /> : ((team as any).contentCount ?? 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">Content</Typography>
              </Box>
            </Grid>

            <Grid xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <StorageIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4">
                  {isLoading ? <CircularProgress size={24} /> : formatStorageAuto((team as any).storageUsedBytes)}
                </Typography>
                <Typography variant="body2" color="text.secondary">Storage</Typography>
              </Box>
            </Grid>
            
            <Grid xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <AvTimerIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4">{memberCount}</Typography>
                <Typography variant="body2" color="text.secondary">Active Members</Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Subscription Status - Only show to users with management permissions */}
          {canManageSubscription && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Subscription Status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip
                  label={subscription?.name || "No Subscription"}
                  color={subscription ? "success" : "warning"}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {subscription
                    ? `Team has an active ${subscription.name} subscription`
                    : "Team does not have an active subscription"
                  }
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      </Grid>
      
      {/* Quick Actions - Only show if user has at least one permission */}
      {(canInviteMembers || canManageSubscription || canCreateContent) && (
        <Grid xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 2,
              borderColor: 'divider',
              boxShadow: 'none',
              height: '100%',
              '&:hover': { transform: 'none', boxShadow: 'none' }
            }}
          >
            <CardHeader
              avatar={<StarIcon color="warning" />}
              title="Quick Actions"
              subheader="Common tasks you might want to do now"
            />
            <Divider />
            <CardContent sx={{ pt: 2 }}>
              <Stack spacing={1.5}>
                {canInviteMembers && (
                  <Button
                    variant="outlined"
                    startIcon={<PeopleIcon />}
                    onClick={onInviteMember}
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Invite Member
                  </Button>
                )}

                {canManageSubscription && (
                  <Button
                    variant="outlined"
                    startIcon={<StorageIcon />}
                    onClick={onManageSubscription}
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Manage Subscription
                  </Button>
                )}

                {canCreateContent && onCreateContent && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={onCreateContent}
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Add Content
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default TeamSummary;